/**
 * مسدود کردنِ کاربر — جدولِ `blocks` (از 0001) و RPCهای مایگریشن 0146.
 *
 * ═══ چرا این‌جا تقریباً هیچ منطقی نیست ═══
 *
 * بلاک شش کار با هم است (ردیفِ `blocks`، قطعِ دوستی، رد کردنِ درخواستِ باز،
 * پاک کردنِ `friendship_prefs` و کشف‌های مخاطبین، و بیرون رفتن از گروه‌های
 * هر دو طرف) و نصفه‌اش از هیچ بدتر است: ردیفِ بلاک بدونِ قطعِ دوستی یعنی
 * کسی که فکر می‌کند بلاک کرده و هنوز در فهرستِ دوستانِ طرف است. پس همه‌ش
 * یک تراکنشِ SQL است — همان قاعده‌ی `profileRepo.deleteMine`.
 *
 * ═══ چرا `block` چیزی برنمی‌گرداند ═══
 *
 * تا WHOSUP-262 فهرستِ «گروه‌های مشترک» را می‌داد و رابط می‌پرسید «از این‌ها
 * هم جدا شوی؟». گروه ولی شخصی است — مالِ سازنده‌اش — و آن فهرست نامِ
 * گروه‌های طرفِ مقابل را به بلاک‌کننده لو می‌داد. حالا خودِ `block_user`
 * (مایگریشن 0154) بی‌صدا هر کدام را از گروه‌های دیگری برمی‌دارد، همان‌طور که
 * `remove_friend` می‌کند، و هیچ فهرستی نمی‌دهد.
 *
 * ═══ چرا `listBlocked` از یک RPC می‌آید و نه از یک joinِ ساده ═══
 *
 * سیاستِ `p_sel` (همان 0146) پروفایلِ بلاک‌شده را از دیدِ بلاک‌کننده هم
 * پنهان می‌کند — و باید بکند، وگرنه «پیدا نشد» فقط یک‌طرفه بود. پس یک
 * `blocks.select('*, profile:profiles(...)')` اسم و عکس را `null` می‌گرفت.
 */
import { supabase } from '../lib/supabase';
import { isDemoMode } from '../lib/demo';
import { avatarUrlOf } from './socialRepo';
import type { UserId } from '../domain/types';

/* eslint-disable @typescript-eslint/no-explicit-any */

/** یک ردیفِ صفحه‌ی «کاربرانِ مسدودشده». */
export interface BlockedUser {
  id: UserId;
  username: string;
  displayName: string;
  avatarPath: string | null;
  avatarUrl: string | null;
  blockedAt: string;
}

export const blockRepo = {
  /**
   * مسدود کن — همه‌ی اثرها، از جمله گروه‌ها، در خودِ دیتابیس. چیزی برای
   * نشان دادن برنمی‌گردد؛ رابط فقط کوئری‌های `BLOCK_STALE_QUERIES` را تازه
   * می‌کند.
   */
  async block(target: UserId): Promise<void> {
    // دمو نه دیتابیس دارد نه رابطه‌ی واقعی؛ دکمه کار می‌کند و چیزی جایی
    // نمی‌رود — همان کاری که بقیه‌ی نوشتن‌های دمو می‌کنند.
    if (isDemoMode) return;
    const { error } = await supabase.rpc('block_user', { p_target: target });
    if (error) throw error;
  },

  /**
   * رفعِ مسدودیت. دوستی خودکار برنمی‌گردد — آن تصمیمِ تازه‌ای است.
   *
   * ولی هر دو طرف دوباره می‌توانند درخواست بدهند (WHOSUP-261، و برای
   * مسیرهای خودکار WHOSUP-262). سرِ WHOSUP-230 این عمداً بسته مانده بود —
   * `friendships_pair` یک ردیف برای هر جفت جا دارد و آن ردیف روی `removed`
   * می‌ماند — و صاحبِ محصول در WHOSUP-261 بازش کرد. راهش `request_friendship`
   * است (مایگریشن 0152، تکمیل در 0153) که همان ردیف را دوباره «در انتظار»
   * می‌کند و اعلانش را هم می‌سازد؛ نگاه کن به `sendRequest` در `socialRepo`.
   */
  async unblock(target: UserId): Promise<void> {
    if (isDemoMode) return;
    const { error } = await supabase.rpc('unblock_user', { p_target: target });
    if (error) throw error;
  },

  async listBlocked(): Promise<BlockedUser[]> {
    if (isDemoMode) return [];
    const { data, error } = await supabase.rpc('list_blocked_users');
    if (error) throw error;
    return (data ?? []).map((r: any): BlockedUser => ({
      id: String(r.user_id) as UserId,
      username: r.username ?? '',
      displayName: r.display_name ?? '',
      avatarPath: r.avatar_path ?? null,
      avatarUrl: avatarUrlOf(r.avatar_path),
      blockedAt: r.blocked_at,
    }));
  },

  /**
   * آیا این یکی را من مسدود کرده‌ام — برای دکمه‌ای که باید «مسدود کردن» یا
   * «رفعِ مسدودیت» بگوید.
   *
   * فقط جهتِ خودم را می‌خواند، چون سیاستِ `b_all` هم بیشتر از این نمی‌دهد:
   * «او مرا بلاک کرده؟» عمداً بی‌جواب می‌ماند.
   */
  async isBlocked(target: UserId): Promise<boolean> {
    if (isDemoMode) return false;
    const { data, error } = await supabase
      .from('blocks').select('blocked_id').eq('blocked_id', target).maybeSingle();
    if (error) throw error;
    return !!data;
  },
};
